<template>
    <div>
        <label
            :for="`category-filter-${categoryId}`"
            class="block py-2 sidebar-navigation-link text-sm cursor-pointer"
        >
            <input
                :id="`category-filter-${categoryId}`"
                v-model="model"
                :value="categoryId"
                type="radio"
                name="category-filter"
                class="hidden radio"
                @change="handleChange"
            >
            <span
                class="block font-semibold leading-6 brand-page-color-active:text-brand-page-color"
                :class="isSelected ? 'text-base-content' : 'text-base-content-subtle'"
            >
                {{ categoryName }}
            </span>
        </label>
        <div v-if="showChildren" class="pl-2 pr-2">
            <ProductListingFilterCategory
                v-for="childCategoryId in Object.keys(data.children)"
                :key="childCategoryId"
                v-model="model"
                :data="data.children[childCategoryId]!"
                :current-category-id="currentCategoryId"
                @change="handleChange"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = defineProps<{
    data: Schemas['CategoryItem']
    currentCategoryId?: string
}>()

const emit = defineEmits<{
    (event: 'change'): void
}>()

const model = defineModel<string[] | string>()

const categoryId = computed(() => {
    return isCurrentCategory.value ? props.currentCategoryId! : props.data.category.id
})

const categoryName = computed(() => {
    return isCurrentCategory.value
        ? findCurrentCategory()?.translated?.name ?? props.data.category.translated.name
        : props.data.category.translated.name
})

const isCurrentCategory = computed(() => {
    return props.data.category.id === props.currentCategoryId || hasCurrentCategoryInChildren()
})

const isSelected = computed(() => {
    return model.value === props.data.category.id || model.value === props.currentCategoryId
})

const showChildren = computed(() => {
    const hasChildren = props.data.children != null && Object.keys(props.data.children).length > 0
    const isThisSelected = model.value === props.data.category.id
    const childIsSelected = isChildSelected(props.data)

    return hasChildren && !isCurrentCategory.value && (isThisSelected || childIsSelected)
})

function findCurrentCategory(): Schemas['CategoryItem']['category'] | null {
    if (props.data.category.id === props.currentCategoryId) {
        return props.data.category
    }

    for (const childId of Object.keys(props.data.children)) {
        if (childId === props.currentCategoryId) {
            return props.data.children[childId]!.category
        }
    }

    return null
}

function hasCurrentCategoryInChildren(): boolean {
    return Object.keys(props.data.children).includes(props.currentCategoryId ?? '')
}

function isChildSelected(data: Schemas['CategoryItem']): boolean {
    if (data.children == null || Object.keys(data.children).length === 0) {
        return false
    }

    for (const childId of Object.keys(data.children)) {
        if (childId === model.value) {
            return true
        }

        if (isChildSelected(data.children[childId]!)) {
            return true
        }
    }

    return false
}

function handleChange() {
    emit('change')
}
</script>
